import React, { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, ScanLine, User, Ticket, LogIn, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";

function GuestListSearch({ onOpenTicket }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const search = async (e) => {
    e?.preventDefault?.();
    if (q.trim().length < 2) return toast.error("Type at least 2 characters");
    setBusy(true);
    try {
      const { data } = await api.get(`/scanner/search?q=${encodeURIComponent(q.trim())}`);
      setResults(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Search failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={search} className="glass p-4" data-testid="guest-list-search">
      <div className="chip !text-magenta !border-magenta/40 mb-2"><Search className="w-3 h-3" /> Guest list — QR won't scan?</div>
      <div className="flex gap-2">
        <input data-testid="guest-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone, email or ticket code" className="input-dark flex-1" />
        <button disabled={busy} className="btn-pink" data-testid="guest-search-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </div>
      {results && (
        <div className="mt-3" data-testid="guest-search-results">
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted mb-1.5">{results.count} match{results.count === 1 ? "" : "es"} for "{results.query}"</div>
          {results.count === 0 ? (
            <div className="text-sm text-muted font-mono">No matching tickets — check the spelling, or the ticket may belong to another event.</div>
          ) : (
            <div className="grid gap-1.5 max-h-72 overflow-y-auto no-scrollbar">
              {results.results.map((r) => (
                <button type="button" key={r.ticket_code} onClick={() => onOpenTicket(r.ticket_code)}
                  data-testid={`guest-result-${r.ticket_code}`}
                  className="text-left rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-lime/40 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-sm truncate">{r.attendee_name || r.attendee_email}</div>
                      <div className="text-[10px] font-mono text-muted truncate">{r.attendee_phone || r.attendee_email || "—"} · {r.tier || "General"} · {r.event_title}</div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full font-mono text-[9px] uppercase shrink-0 ${r.remaining > 0 && r.status === "valid" ? "bg-lime text-ink" : r.status !== "valid" ? "bg-magenta text-white" : "bg-orangex text-white"}`}>
                      {r.status !== "valid" ? r.status : r.remaining > 0 ? `${r.remaining}/${r.admissions_total} left` : "used up"}
                    </span>
                  </div>
                  <div className="mt-1.5 text-[10px] font-mono text-skyx">Tap to open & check in →</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

function LiveCounts({ stats, loading, onRefresh }) {
  if (loading) return <div className="glass p-4 font-mono text-xs text-muted" data-testid="scanner-stats-loading">Loading gate counts…</div>;
  if (!stats?.length) return null;
  return (
    <div className="glass p-4" data-testid="scanner-stats">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-lime">Live at the gate</div>
        <button onClick={onRefresh} data-testid="scanner-stats-refresh" className="btn-ghost !py-1.5 !px-3 !text-[10px]"><RefreshCw className="w-3 h-3" /> Refresh</button>
      </div>
      <div className="grid gap-2">
        {stats.map((s) => {
          const pct = s.sold > 0 ? Math.round((s.checked_in / s.sold) * 100) : 0;
          return (
            <div key={s.id} data-testid={`scanner-stat-${s.id}`}>
              <div className="flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm truncate">{s.title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted">{s.city} · {s.date}</div>
                </div>
                <div className="font-mono text-xs shrink-0">
                  <span className="text-lime font-bold" data-testid={`scanner-stat-in-${s.id}`}>{s.checked_in}</span>
                  <span className="text-muted"> / {s.sold} in</span>
                  <span className="text-pink"> · {s.remaining} left</span>
                </div>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full bg-lime transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function QRScanner() {
  const { user, loading } = useAuth();
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null);
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const scannerRef = useRef(null);
  const lockRef = useRef(false);
  const containerId = "qr-reader";

  const loadStats = React.useCallback(() => {
    api.get("/scanner/stats")
      .then(({ data }) => setStats(data || []))
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    if (!user || !["organizer", "admin", "checker"].includes(user.role)) return;
    loadStats();
  }, [user, loadStats]);

  useEffect(() => {
    if (!scanning) return;
    let scanner;
    (async () => {
      const { Html5Qrcode } = await import("html5-qrcode");
      try {
        scanner = new Html5Qrcode(containerId);
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 260 } },
          async (decodedText) => {
            if (lockRef.current) return;
            lockRef.current = true;
            try { scanner.pause(true); } catch { /* noop */ }
            await verify(decodedText);
          },
          () => {}
        );
      } catch (e) { toast.error("Could not start camera. Grant camera permission."); setScanning(false); }
    })();
    return () => {
      lockRef.current = false;
      if (scanner) scanner.stop().then(() => scanner.clear()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  if (loading) return <Layout><div className="p-10 font-display text-3xl">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (!["organizer", "admin", "checker"].includes(user.role)) return <Navigate to="/" replace />;

  const verify = async (qrData) => {
    setCheckedIn(null);
    try {
      const { data } = await api.post("/tickets/verify", { qr_data: qrData });
      setResult({ ...data, qr: qrData });
      if (!data.valid) toast.error(data.message || "Invalid ticket");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "Scan failed";
      toast.error(msg);
      setResult({ valid: false, reason: "error", message: msg });
    }
  };

  const confirmCheckIn = async () => {
    if (!result?.qr) return;
    setChecking(true);
    try {
      const { data } = await api.post("/tickets/scan", { qr_data: result.qr });
      if (data.valid) {
        setCheckedIn(data);
        toast.success("Checked in");
        loadStats();
      } else {
        toast.error(data.message || "Could not check in");
        setResult({ ...result, ...data, qr: result.qr });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Check-in failed");
    } finally { setChecking(false); }
  };

  const nextScan = () => {
    setResult(null); setCheckedIn(null); setManual("");
    lockRef.current = false;
    try { scannerRef.current?.resume(); } catch { /* noop */ }
  };

  const scanManual = async () => {
    if (!manual.trim()) return;
    let qrData = manual.trim();
    if (!qrData.startsWith("{")) qrData = JSON.stringify({ code: qrData });
    lockRef.current = true;
    await verify(qrData);
  };

  const t = result?.ticket;
  const showEntry = checkedIn || result;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="chip !text-lime !border-lime/40 mb-3">Entry Scanner</div>
        <h1 className="font-display text-4xl md:text-5xl mb-2">Scan tickets at the gate.</h1>
        <p className="text-muted text-sm">
          {user.role === "checker" ? "You can scan only the events allocated to you." : "Scan, check the attendee, then confirm check-in. One ticket at a time."}
        </p>

        <div className="mt-6 grid gap-4">
          <LiveCounts stats={stats} loading={statsLoading} onRefresh={loadStats} />
          {!result && (!scanning ? (
            <button data-testid="scan-start" onClick={() => { setResult(null); lockRef.current = false; setScanning(true); }} className="btn-primary w-full justify-center !py-4"><ScanLine className="w-5 h-5" /> Start Camera Scanner</button>
          ) : (
            <div className="glass p-3">
              <div id={containerId} className="rounded-2xl overflow-hidden bg-black" style={{ minHeight: 320 }} />
              <button data-testid="scan-stop" onClick={() => setScanning(false)} className="mt-3 btn-ghost w-full justify-center">Stop Scanner</button>
            </div>
          ))}

          {!result && (
            <div className="glass p-4">
              <div className="chip !text-skyx !border-skyx/40 mb-2">Or enter the code</div>
              <div className="flex gap-2">
                <input data-testid="scan-manual-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Ticket code (e.g. EV-ABC12345)" className="input-dark flex-1" />
                <button data-testid="scan-manual-btn" onClick={scanManual} className="btn-lime">Look up</button>
              </div>
            </div>
          )}

          {!result && (
            <GuestListSearch onOpenTicket={async (code) => {
              lockRef.current = true;
              await verify(JSON.stringify({ code }));
            }} />
          )}

          {showEntry && (
            <div className={`glass p-6 border-2 ${checkedIn ? "!border-lime" : result.valid ? "!border-skyx" : result.reason === "already_entered" ? "!border-orangex" : "!border-magenta"}`} data-testid="scan-result">
              <div className="flex items-start gap-3">
                {checkedIn ? <CheckCircle2 className="w-12 h-12 text-lime shrink-0" />
                  : result.valid ? <Ticket className="w-12 h-12 text-skyx shrink-0" />
                  : result.reason === "already_entered" ? <AlertTriangle className="w-12 h-12 text-orangex shrink-0" />
                  : <XCircle className="w-12 h-12 text-magenta shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-display text-3xl">
                    {checkedIn ? "Checked In" : result.valid ? "Ticket Valid" : result.reason === "already_entered" ? "Already Entered" : "Rejected"}
                  </div>
                  <div className="text-sm text-muted mt-1" data-testid="scan-result-message">{(checkedIn || result).message}</div>

                  {t && (
                    <div className="mt-4 grid gap-2" data-testid="scan-attendee">
                      <div className="glass !rounded-xl p-3">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted mb-1">Attendee</div>
                        <div className="font-heading font-bold text-lg flex items-center gap-2"><User className="w-4 h-4 text-lime" />{result.attendee?.name || t.user_email || "—"}</div>
                        <div className="text-xs text-muted mt-0.5">{result.attendee?.email || t.user_email}{result.attendee?.mobile ? ` · ${result.attendee.mobile}` : ""}</div>
                      </div>
                      <div className="font-mono text-xs text-muted grid gap-1">
                        <div><span className="text-white">Event:</span> {t.event_title}</div>
                        <div><span className="text-white">Code:</span> <span className="text-lime">{t.ticket_code}</span></div>
                        <div><span className="text-white">Tier:</span> {t.tier} × {t.quantity}</div>
                      </div>
                      {typeof (checkedIn || result).admissions_total === "number" && (
                        <div className="grid grid-cols-3 gap-2" data-testid="scan-admissions">
                          <div className="glass !rounded-xl p-2 text-center"><div className="font-display text-2xl">{(checkedIn || result).admissions_total}</div><div className="text-[10px] uppercase text-muted">Total</div></div>
                          <div className="glass !rounded-xl p-2 text-center"><div className="font-display text-2xl text-lime">{(checkedIn || result).admissions_used}</div><div className="text-[10px] uppercase text-muted">Checked in</div></div>
                          <div className="glass !rounded-xl p-2 text-center"><div className="font-display text-2xl text-pink">{(checkedIn || result).remaining}</div><div className="text-[10px] uppercase text-muted">Remaining</div></div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-5 flex flex-wrap gap-2">
                    {result.valid && !checkedIn && (
                      <button onClick={confirmCheckIn} disabled={checking} data-testid="scan-checkin-btn" className="btn-lime !py-3 !px-5">
                        {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Check in
                      </button>
                    )}
                    <button onClick={nextScan} data-testid="scan-next-btn" className="btn-ghost !py-3 !px-5"><ScanLine className="w-4 h-4" /> Scan next ticket</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
